import { module } from 'modujs';
import flatpickr from 'flatpickr';
import { english as English } from 'flatpickr/dist/l10n/default.js';
import { French } from 'flatpickr/dist/l10n/fr.js';
import rangePlugin from 'flatpickr/dist/plugins/rangePlugin.js'
import { html, isMobile } from '../utils/environment';

if (html.lang === 'fr') {
    flatpickr.localize(French);
} else if (html.lang === 'iu') {
    const Inuktitut = Object.assign({}, English);
    Inuktitut.months.longhand = [
        'ᔮᓄᐊᕆ',    // January
        'ᕕᕝᕗᐊᕆ',  // February
        'ᒫᑦᓯ',      // March
        'ᐄᐳᕈ',     // April
        'ᒪᐃ',      // May
        'ᔫᓂ',      // June
        'ᔪᓚᐃ',     // July
        'ᐋᒡᒌᓯ',    // August
        'ᓯᑎᐱᕆ',   // September
        'ᐅᑐᐱᕆ',   // October
        'ᓄᕕᐱᕆ',  // November
        'ᑎᓯᐱᕆ',   // December
    ];

    flatpickr.localize(Inuktitut);
}

export default class extends module {
    /** @var {HTMLInputElement} Alias of {@see this.el}. */
    startDate;

    /** @var {?HTMLInputElement} The related input specified by `[data-date-picker-range]`. */
    endDate;

    constructor(options) {
        super(options);

        this.startDate = options.el;
    }

    init() {
        if (isMobile) {
            this.#initNative();
        } else {
            this.#initFlatpickr();
        }
    }

    #initFlatpickr() {
        const options = this.#parseFlatpickrOptions({
            minDate: this.startDate.getAttribute('min'),
            maxDate: this.startDate.getAttribute('max'),
        });

        this.flatpickr = flatpickr(this.startDate, options);
    }

    #parseFlatpickrOptions(options) {
        const relatedSelector = this.getData('range');

        if (relatedSelector == null) {
            return options;
        }

        if (relatedSelector === '') {
            options.mode = 'range';
            return options;
        }

        const endDate = document.querySelector(relatedSelector);
        if (endDate) {
            if (endDate instanceof HTMLInputElement) {
                /**
                 * Flatpickr should add a class to identify
                 * the secondary input as Flatpickr-enhanced.
                 * This class is used to adjust `[readonly]` styles.
                 */
                endDate.classList.add('flatpickr-related');

                options.onReady = function (_selectedDates, _dateStr, fp) {
                    if (fp.isMobile) {
                        /**
                         * Flatpickr's Range plugin does not support its
                         * `isMobile` condition and fallback behaviour.
                         */
                        endDate.removeAttribute('readonly');
                    } else {
                        /**
                         * Flatpickr should be in charge of converting
                         * the input to text but is not because of
                         * the beta status of the range plugin.
                         */
                        endDate.type = 'text';
                    }
                };
            }

            options.plugins = [ new rangePlugin({ input: endDate }) ];
        }

        return options;
    }

    #initNative() {
        const relatedSelector = this.getData('range');
        if (relatedSelector == null) {
            return;
        }

        this.endDate = document.querySelector(relatedSelector);
        if (!this.endDate) {
            return;
        }

        this.onChangeMaxDate = this.onChangeMaxDate.bind(this);
        this.onChangeMinDate = this.onChangeMinDate.bind(this);

        this.startDate.addEventListener('change', this.onChangeMinDate);
        this.endDate.addEventListener('change', this.onChangeMaxDate);

        this.startDate._max = this.startDate.max;
        this.endDate._max   = this.endDate.max;

        this.startDate._min = this.startDate.min;
        this.endDate._min   = this.endDate.min;

        this.changeMaxDate(this.endDate.value);
        this.changeMinDate(this.startDate.value);
    }

    /**
     * @param {Date|string} max - The max date for the start date.
     */
    changeMaxDate(max) {
        if (this.startDate._max && this.startDate._max < max) {
            max = this.startDate._max;
        }

        this.startDate.max = max;
    }

    /**
     * @param {Date|string} min - The min date for the end date.
     */
    changeMinDate(min) {
        if (this.endDate._min && this.endDate._min > min) {
            min = this.endDate._min;
        }

        this.endDate.min = min;
    }

    /**
     * @param {Event} event - The 'change' event.
     */
    onChangeMaxDate({ target }) {
        this.changeMaxDate(target.value);
    }

    /**
     * @param {Event} event - The 'change' event.
     */
    onChangeMinDate({ target }) {
        this.changeMinDate(target.value);
    }

    destroy() {
        this.flatpickr?.destroy?.();
    }
}
